import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import useIsMobile from '../../../hooks/useIsMobile'
import Modal from '../../ui/modal'
import { FormContainer, FormSubtitleBold } from '../../ui/form'
import RadioButtonList from '../../ui/radioButton'
import { Divider, FormSection } from '../../ui/form/styled'

import Input from '../../ui/input'
import Select from '../../ui/select'
import Autocomplete from '../../ui/autoComplete'
import DateRangeInput from '../../ui/input/dateRangeInput'
import DateTimeRangeFilters from '../../forms/dateTimeRangeFilters'
import { Button } from '../../ui/button'
import { formatDateInput } from '../../../utils/formatDate'
import MultiSelect from '../../ui/multiSelect'
import { DateRangeCalendarWrapper } from './styled'
import 'react-datepicker/dist/react-datepicker.css'

registerLocale('es', es)

const parseDateValue = value => {
	if (!value) return null
	const parsed = moment(value, 'DD/MM/YYYY', true)
	return parsed.isValid() ? parsed.toDate() : null
}

const DEFAULT_SORT_OPTIONS = [
	{ id: null, label: 'Fecha' },
	{ id: 'clinica', label: 'Clínica' },
	{ id: 'nroOrden', label: 'Número de orden' }
]

const getDefaultValue = field =>
	field.type === 'multiselect' ? [] : field.type === 'number' ? null : ''

const ModalFilter = ({
	showModal,
	onClose,
	onApplyFilters,
	clinics = [],
	initialFilters,
	sortOptions = DEFAULT_SORT_OPTIONS,
	filterFields,
	showSort = true,
	center = true,
	showTitleMobile = false,
	stickyButtonsMobile = false,
	keepDraftOnClose = false
}) => {
	const isMobile = useIsMobile()

	const clinicsOptions = clinics.map(clinic => ({
		value: `${clinic.id}`,
		label: clinic.nombre || clinic.clinicName
	}))

	const fields = filterFields || [
		{
			type: 'multiselect',
			name: 'clinic',
			label: 'Filtrar por clínica',
			placeholder: 'Seleccioná una clínica',
			options: clinicsOptions
		},
		{
			type: 'date',
			name: 'fecha',
			label: 'Filtrar por fecha',
			placeholder: 'Ingresá la fecha de tu informe'
		},
		{
			type: 'number',
			name: 'order',
			label: 'Filtrar por número de orden',
			placeholder: 'Ingresá tu número de orden'
		}
	]

	const [sortBy, setSortBy] = useState(null)
	const [values, setValues] = useState({})
	const titleAlign = center ? 'center' : 'left'
	const hasInitializedRef = useRef(false)

	useEffect(() => {
		if (!showModal) return
		// Con keepDraftOnClose conservamos la selección del usuario aunque cierre sin aplicar.
		if (keepDraftOnClose && hasInitializedRef.current) return
		hasInitializedRef.current = true
		setSortBy(initialFilters?.sortBy ?? null)
		const initialValues = { ...(initialFilters || {}) }
		fields.forEach(field => {
			if (field.type === 'dateTimeRange') {
				return
			}

			if (field.type === 'daterange') {
				initialValues[field.startField] = parseDateValue(initialFilters?.[field.startField])
				initialValues[field.endField] = parseDateValue(initialFilters?.[field.endField])
				return
			}

			const fromInitial = initialFilters?.[field.name]
			initialValues[field.name] =
				fromInitial !== undefined && fromInitial !== null
					? fromInitial
					: getDefaultValue(field)
		})
		setValues(initialValues)
	}, [showModal])

	const setFieldValue = (name, value) => {
		setValues(prev => ({ ...prev, [name]: value }))
	}

	const handleApplyFilters = () => {
		const filters = showSort ? { ...values, sortBy } : { ...values }
		if (typeof onApplyFilters === 'function') {
			onApplyFilters(filters)
		}
		if (onClose) onClose()
	}

	const renderField = field => {
		const value = values[field.name]
		const dynamicFieldProps =
			typeof field.getProps === 'function' ? field.getProps(values) || {} : field.props || {}

		switch (field.type) {
			case 'multiselect':
				return (
					<MultiSelect
						key={field.name}
						padding="10px"
						label={field.label}
						name={field.name}
						value={value || []}
						onChange={e => {
							const newValues = e?.target?.value ?? e?.value ?? e
							setFieldValue(field.name, Array.isArray(newValues) ? newValues : [])
						}}
						options={field.options || []}
						placeholder={field.placeholder}
					/>
				)
			case 'select':
				return (
					<Select
						key={field.name}
						label={field.label}
						name={field.name}
						value={value || ''}
						onChange={e => {
							const hasTargetValue = e && e.target && Object.prototype.hasOwnProperty.call(e.target, 'value')
							const newValue = hasTargetValue ? e.target.value : e?.value ?? e
							setFieldValue(field.name, newValue)
						}}
						options={field.options || []}
						placeholder={field.placeholder}
						isClearable={field.isClearable}
					/>
				)
			case 'dateTimeRange':
				return (
					<DateTimeRangeFilters
						key={field.name || 'dateTimeRange'}
						values={values}
						setFieldValue={setFieldValue}
						textAlign={titleAlign}
						{...dynamicFieldProps}
					/>
				)
			case 'autocomplete':
				return (
					<Autocomplete
						key={field.name}
						label={field.label}
						name={field.name}
						value={value || ''}
						onChange={e => {
							const newValue = e?.target?.value ?? e?.value ?? e
							setFieldValue(field.name, newValue)
						}}
						options={field.options || []}
						placeholder={field.placeholder}
					/>
				)
			case 'daterange':
				return (
					<DateRangeCalendarWrapper key={field.name}>
						<DatePicker
							selectsRange
							startDate={values[field.startField] || null}
							endDate={values[field.endField] || null}
							onChange={dates => {
								const [start, end] = dates
								setValues(prev => ({ ...prev, [field.startField]: start, [field.endField]: end }))
							}}
							showMonthDropdown
							showYearDropdown
							dropdownMode="select"
							yearDropdownItemNumber={15}
							scrollableYearDropdown
							locale="es"
							dateFormat="dd/MM/yyyy"
							placeholderText={field.placeholder}
							customInput={
								<DateRangeInput
									label={field.label}
									onClear={() =>
										setValues(prev => ({
											...prev,
											[field.startField]: null,
											[field.endField]: null
										}))
									}
								/>
							}
						/>
					</DateRangeCalendarWrapper>
				)
			case 'date':
				return (
					<Input
						key={field.name}
						fullWidth
						label={field.label}
						placeholder={field.placeholder}
						value={value || ''}
						onChange={e => setFieldValue(field.name, formatDateInput(e.target.value))}
						inputMode="numeric"
						pattern="\d*"
						maxLength={field.maxLength || 10}
					/>
				)
			case 'number':
				return (
					<Input
						key={field.name}
						fullWidth
						label={field.label}
						placeholder={field.placeholder}
						value={value || ''}
						onChange={e => {
							const v = e.target.value
							if (/^\d*$/.test(v)) setFieldValue(field.name, v)
						}}
						inputMode="numeric"
						pattern="[0-9]*"
					/>
				)
			default:
				return (
					<Input
						key={field.name}
						fullWidth
						label={field.label}
						placeholder={field.placeholder}
						value={value || ''}
						onChange={e => setFieldValue(field.name, e.target.value)}
					/>
				)
		}
	}

	if (!showModal) return null

	return (
		<Modal
			isFullHeight={isMobile}
			onClose={onClose}
			withComponentContainer
			buttons={
				<Button label="Aplicar filtros" $variant="primary" onClick={() => handleApplyFilters()} />
			}
			stickyButtonsMobile={stickyButtonsMobile}
			withMainSection={true}
		>
			<FormContainer
				align="center"
				margin="0 auto"
				justifyMobile="start"
				paddingMobile="0px"
			>
				{showSort && (
					<>
						<FormSection>
							{ (
								<FormSubtitleBold align={titleAlign} font="24">
									{isMobile ? 'Ordenar por' : 'Ordenar'}
								</FormSubtitleBold>
							)}
							<RadioButtonList options={sortOptions} selected={sortBy} onChange={setSortBy} />
						</FormSection>
						<Divider />
					</>
				)}
				<FormSection mb={16}>
					{(showTitleMobile && isMobile) && (
						<FormSubtitleBold align={titleAlign} font="24">
							Filtros
						</FormSubtitleBold>
					)}
					{!isMobile && (
						<FormSubtitleBold align={titleAlign} font="24">
							Filtrar
						</FormSubtitleBold>
					)}
					{fields.map(renderField)}
				</FormSection>
			</FormContainer>
		</Modal>
	)
}

export default ModalFilter
